import React from "react";

// Renders a publication cover — magazine, book, or poster — as pure CSS.
// Each variant is deliberately different so the grid feels varied and real.
export default function Cover({ item }) {
  if (item.coverType === "magazine") return <MagazineCover item={item} />;
  if (item.coverType === "book") return <BookCover item={item} />;
  if (item.coverType === "poster") return <PosterCover item={item} />;
  return null;
}

function MagazineCover({ item }) {
  return (
    <div className="relative aspect-[3/4] overflow-hidden border border-mist bg-ink">
      <img
        src={item.image}
        alt=""
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/15 to-ink/85" />

      {/* Masthead */}
      <div className="absolute top-0 left-0 right-0 flex items-baseline justify-between px-4 md:px-5 pt-4 md:pt-5 text-paper">
        <div
          className={`font-display leading-none tracking-widest2 ${
            item.mastheadSize || "text-xl md:text-3xl"
          }`}
        >
          {item.source}
        </div>
        <div className="text-[9px] md:text-[10px] tracking-widest3 uppercase opacity-80">
          {item.issueLabel || `No. ${item.issueNo} · ${item.year}`}
        </div>
      </div>
      <div className="absolute top-[52px] md:top-[62px] left-4 right-4 md:left-5 md:right-5 h-px bg-paper/40" />

      {/* Cover line */}
      <div className="absolute bottom-0 left-0 right-0 px-4 md:px-5 pb-4 md:pb-5 text-paper">
        <div className="text-[9px] md:text-[10px] tracking-widest3 uppercase opacity-80 mb-2">
          {item.type}
        </div>
        <div className="font-display text-xl md:text-2xl tracking-widest2 leading-[1.05]">
          {item.title}
        </div>
        {item.subtitle && (
          <div className="mt-1.5 text-[10px] md:text-[11px] tracking-widest3 uppercase opacity-75 leading-relaxed">
            {item.subtitle}
          </div>
        )}
      </div>
    </div>
  );
}

function BookCover({ item }) {
  const bg = item.bg || "bg-[#0E0E0E]";
  return (
    <div className={`relative aspect-[3/4] overflow-hidden border border-mist ${bg} text-paper`}>
      <div className="absolute inset-3 md:inset-4 border border-paper/25 flex flex-col">
        <div className="px-4 md:px-6 pt-4 md:pt-6 flex items-center justify-between text-[9px] md:text-[10px] tracking-widest3 uppercase text-paper/60">
          <span>{item.source}</span>
          <span>{item.year}</span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-4 md:px-6 text-center">
          <div className="font-display text-3xl md:text-4xl tracking-widest2 leading-[0.95]">
            {item.title}
          </div>
          <div className="w-10 h-px bg-paper/60 my-4 md:my-5" />
          {item.subtitle && (
            <div className="text-[10px] md:text-xs tracking-widest3 uppercase text-paper/75 leading-relaxed max-w-[90%]">
              {item.subtitle}
            </div>
          )}
        </div>
        <div className="px-4 md:px-6 pb-4 md:pb-6 text-center text-[9px] md:text-[10px] tracking-widest3 uppercase text-paper/60">
          {item.author || item.type}
        </div>
      </div>
    </div>
  );
}

function PosterCover({ item }) {
  return (
    <div className="relative aspect-[3/4] overflow-hidden border border-mist bg-ink text-paper">
      <div className="h-full p-5 md:p-7 flex flex-col justify-between">
        <div>
          <div className="text-[9px] md:text-[10px] tracking-widest3 uppercase text-paper/70 mb-2">
            {item.type} · {item.year}
          </div>
          <div className="w-8 h-px bg-paper/60" />
        </div>
        <div>
          <div className="font-display text-3xl md:text-5xl tracking-widest2 leading-[0.9]">
            {item.title}
          </div>
          {item.subtitle && (
            <div className="mt-3 text-[10px] md:text-xs tracking-widest3 uppercase text-paper/70 leading-relaxed max-w-[90%]">
              {item.subtitle}
            </div>
          )}
        </div>
        <div>
          <div className="w-8 h-px bg-paper/60 mb-2" />
          <div className="text-[9px] md:text-[10px] tracking-widest3 uppercase text-paper/70">
            {item.source}
          </div>
        </div>
      </div>
    </div>
  );
}
